// src/pages/PublicProfileForm.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getMyBusinesses,
  getMyBusiness,
  updatePublicProfile,
  uploadPublicProfileImage,
  MyBusiness,
  PublicProfilePayload,
} from "../services/api";
import "./PublicProfileForm.scss";

/** Valeurs par défaut pour tous les champs publics */
const DEFAULT_PAYLOAD: PublicProfilePayload = {
  enabled: false,
  slug: "",
  tagline: "",
  description: "",
  website: "",
  phonePublic: "",
  addressStreet: "",
  addressCity: "",
  addressZip: "",
  addressCountry: "",
  hoursText: "",
  logoUrl: "",
  coverUrl: "",
  facebook: "",
  instagram: "",
  tiktok: "",
  whatsapp: "",
  categories: [],
  allowIndexing: true,
};

const catSuggestion = [
  "restaurant",
  "bar",
  "boulangerie",
  "coiffeur",
  "plombier",
  "garagiste",
  "hôtel",
  "spa",
  "agence",
  "boutique",
];

const draftKey = (bizId: string) => `rb_public_profile_draft_${bizId}`;

const PublicProfileForm: React.FC = () => {
  const [businesses, setBusinesses] = useState<MyBusiness[]>([]);
  const [bizId, setBizId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [payload, setPayload] = useState<PublicProfilePayload>(DEFAULT_PAYLOAD);

  // Upload progress
  const [uploadingLogo, setUploadingLogo] = useState<number>(0);
  const [uploadingCover, setUploadingCover] = useState<number>(0);

  // ✅ guard to prevent autosave while hydrating
  const [hydrated, setHydrated] = useState(false);

  // 🔑 Local text state for the categories input
  const [categoriesText, setCategoriesText] = useState<string>("");

  // ---------------- Load businesses ----------------
  useEffect(() => {
    (async () => {
      try {
        const list = await getMyBusinesses();
        setBusinesses(list);
        if (list[0]) setBizId(list[0].id);
      } catch (e: any) {
        toast.error(e?.message || "Impossible de charger vos entreprises.");
      }
    })();
  }, []);

  // ---------------- Load selected business profile (+draft) ----------------
  useEffect(() => {
    if (!bizId) return;
    let alive = true;

    (async () => {
      setHydrated(false);
      setLoading(true);
      try {
        const full = await getMyBusiness(bizId);
        if (!alive) return;

        const pp = full.publicProfile || {};
        const serverPayload: PublicProfilePayload = {
          ...DEFAULT_PAYLOAD,
          website: full.website || "",
          ...pp,
          categories: Array.isArray(pp.categories) ? pp.categories : [],
        };

        // Try draft first (if any), else use server values
        let initial = serverPayload;
        try {
          const raw = localStorage.getItem(draftKey(bizId));
          if (raw) {
            const { value } = JSON.parse(raw);
            if (value && typeof value === "object") {
              initial = { ...serverPayload, ...value };
            }
          }
        } catch {
          /* ignore draft parse errors */
        }

        if (alive) setPayload(initial);
      } catch (e: any) {
        if (alive) toast.error(e?.message || "Impossible de charger la fiche.");
      } finally {
        if (alive) {
          setLoading(false);
          setHydrated(true);
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [bizId]);

  // Keep the local categories text in sync with payload


  // ---------------- Draft autosave (only after hydration) ----------------
  useEffect(() => {
    if (!bizId || !hydrated) return;
    try {
      localStorage.setItem(
        draftKey(bizId),
        JSON.stringify({ value: payload, updatedAt: Date.now() })
      );
    } catch {
      /* ignore */
    }
  }, [bizId, payload, hydrated]);

  // ---------------- Helpers ----------------
  const previewReviewUrl = useMemo(() => {
    const s = (payload.slug || "").trim();
    return s ? `/b/${encodeURIComponent(s)}` : "";
  }, [payload.slug]);

  const previewProfileUrl = useMemo(() => {
    const s = (payload.slug || "").trim();
    return s ? `/p/${encodeURIComponent(s)}` : "";
  }, [payload.slug]);

  function onChange<K extends keyof PublicProfilePayload>(
    key: K,
    val: PublicProfilePayload[K]
  ) {
    setPayload((p) => ({ ...p, [key]: val }));
  }

  // Update both the local text and the normalized array in payload
  function onCategoriesTextChange(text: string) {
    setCategoriesText(text);
    const arr = text
      .split(",")
      .map((x) => x.trim())
      .filter((x) => x.length > 0);
    onChange("categories", arr);
  }

  // ---------------- Upload via backend → Cloudinary ----------------
  async function handleImageUpload(
    kind: "logoUrl" | "coverUrl",
    file?: File
  ) {
    try {
      if (!bizId || !file) return;
      if (kind === "logoUrl") setUploadingLogo(1);
      else setUploadingCover(1);

      const type = kind === "logoUrl" ? "logo" : "cover";
      const { url } = await uploadPublicProfileImage(
        bizId,
        file,
        type,
        (pct) =>
          kind === "logoUrl" ? setUploadingLogo(pct) : setUploadingCover(pct)
      );

      onChange(kind, url as any);
      toast.success(
        kind === "logoUrl" ? "Logo mis à jour." : "Couverture mise à jour."
      );
    } catch (e: any) {
      toast.error(e?.message || "Échec de l’upload.");
    } finally {
      if (kind === "logoUrl") setUploadingLogo(0);
      else setUploadingCover(0);
    }
  }

  // ---------------- Save ----------------
  async function onSave() {
    if (!bizId) return;
    const s = (payload.slug || "").trim();
    if (payload.enabled && s.length < 3) {
      toast.warn("Le slug doit faire au moins 3 caractères.");
      return;
    }
    setSaving(true);
    try {
      const body: PublicProfilePayload = {
        enabled: !!payload.enabled,
        slug: s || undefined,

        // branding
        tagline: (payload.tagline || "").trim(),
        description: (payload.description || "").trim(),
        logoUrl: (payload.logoUrl || "").trim(),
        coverUrl: (payload.coverUrl || "").trim(),

        // contact & web
        website: (payload.website || "").trim(),
        phonePublic: (payload.phonePublic || "").trim(),

        // address
        addressStreet: (payload.addressStreet || "").trim(),
        addressCity: (payload.addressCity || "").trim(),
        addressZip: (payload.addressZip || "").trim(),
        addressCountry: (payload.addressCountry || "").trim(),

        // hours
        hoursText: (payload.hoursText || "").trim(),

        // socials
        facebook: (payload.facebook || "").trim(),
        instagram: (payload.instagram || "").trim(),
        tiktok: (payload.tiktok || "").trim(),
        whatsapp: (payload.whatsapp || "").trim(),

        // taxonomy / SEO
        categories: payload.categories || [],
        allowIndexing: !!payload.allowIndexing,
      };

      await updatePublicProfile(bizId, body);

      // ✅ Keep the form filled: persist what we just saved
      const saved = { ...payload, ...body };
      localStorage.setItem(
        draftKey(bizId),
        JSON.stringify({ value: saved, updatedAt: Date.now() })
      );
      setPayload(saved);

      toast.success("Page publique enregistrée ✅");
    } catch (e: any) {
      toast.error(e?.message || "Échec de l’enregistrement.");
    } finally {
      setSaving(false);
    }
  }

  // ---------------- UI ----------------
  return (
    <div className="publicProfileForm">
      <h1>Page publique</h1>
      <p className="hint">
        Ajoutez les informations visibles dans l’annuaire public et sur votre
        page.
      </p>

      <div className="field">
        <label>Entreprise</label>
        <select
          value={bizId}
          onChange={(e) => setBizId(e.target.value)}
          disabled={loading || saving}
        >
          {businesses.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </div>

      <div className="grid">
        <div className="field">
          <label>
            <input
              type="checkbox"
              checked={!!payload.enabled}
              onChange={(e) => onChange("enabled", e.target.checked)}
              disabled={loading || saving}
            />{" "}
            Activer la page publique
          </label>
        </div>

        <div className="field">
          <label>Slug (URL publique)</label>
          <input
            placeholder="ex: cafe-du-centre"
            value={payload.slug || ""}
            onChange={(e) =>
              onChange(
                "slug",
                e.target.value.replace(/\s+/g, "-").toLowerCase()
              )
            }
            disabled={loading || saving}
          />
          <div className="help">
            Page site web :{" "}
            {previewProfileUrl ? (
              <a href={previewProfileUrl} target="_blank" rel="noreferrer">
                {previewProfileUrl}
              </a>
            ) : (
              "—"
            )}
          </div>
          <div className="help">
            Page avis :{" "}
            {previewReviewUrl ? (
              <a href={previewReviewUrl} target="_blank" rel="noreferrer">
                {previewReviewUrl}
              </a>
            ) : (
              "—"
            )}
          </div>
        </div>

        {/* Logo */}
        <div className="field">
          <label>Logo (URL)</label>
          <div className="row">
            <input
              placeholder="https://…/logo.png"
              value={payload.logoUrl || ""}
              onChange={(e) => onChange("logoUrl", e.target.value)}
              disabled={loading || saving || uploadingLogo > 0}
            />
            <label className="btn-upload">
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) =>
                  handleImageUpload("logoUrl", e.target.files?.[0] || undefined)
                }
              />
              {uploadingLogo ? `Upload ${uploadingLogo}%` : "Uploader"}
            </label>
          </div>
          {payload.logoUrl && (
            <div className="thumb">
              <img src={payload.logoUrl} alt="Logo preview" />
              <button
                type="button"
                className="link"
                onClick={() => onChange("logoUrl", "")}
              >
                Supprimer
              </button>
            </div>
          )}
        </div>

        {/* Cover */}
        <div className="field">
          <label>Couverture (URL)</label>
          <div className="row">
            <input
              placeholder="https://…/cover.jpg"
              value={payload.coverUrl || ""}
              onChange={(e) => onChange("coverUrl", e.target.value)}
              disabled={loading || saving || uploadingCover > 0}
            />
            <label className="btn-upload">
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) =>
                  handleImageUpload("coverUrl", e.target.files?.[0] || undefined)
                }
              />
              {uploadingCover ? `Upload ${uploadingCover}%` : "Uploader"}
            </label>
          </div>
          {payload.coverUrl && (
            <div className="thumb wide">
              <img src={payload.coverUrl} alt="Cover preview" />
              <button
                type="button"
                className="link"
                onClick={() => onChange("coverUrl", "")}
              >
                Supprimer
              </button>
            </div>
          )}
        </div>

        <div className="field">
          <label>Tagline</label>
          <input
            placeholder="ex: Cuisine maison & produits frais"
            value={payload.tagline || ""}
            onChange={(e) => onChange("tagline", e.target.value)}
            disabled={loading || saving}
          />
        </div>

        <div className="field span2">
          <label>Description</label>
          <textarea
            rows={4}
            placeholder="Décrivez votre activité…"
            value={payload.description || ""}
            onChange={(e) => onChange("description", e.target.value)}
            disabled={loading || saving}
          />
        </div>

        <div className="field">
          <label>Site externe (URL)</label>
          <input
            placeholder="https://exemple.com"
            value={payload.website || ""}
            onChange={(e) => onChange("website", e.target.value)}
            disabled={loading || saving}
          />
        </div>

        <div className="field">
          <label>Téléphone public</label>
          <input
            placeholder="+33 1 23 45 67 89"
            value={payload.phonePublic || ""}
            onChange={(e) => onChange("phonePublic", e.target.value)}
            disabled={loading || saving}
          />
        </div>

        <div className="field">
          <label>Adresse (rue)</label>
          <input
            value={payload.addressStreet || ""}
            onChange={(e) => onChange("addressStreet", e.target.value)}
            disabled={loading || saving}
          />
        </div>
        <div className="field">
          <label>Ville</label>
          <input
            placeholder="ex: Lyon"
            value={payload.addressCity || ""}
            onChange={(e) => onChange("addressCity", e.target.value)}
            disabled={loading || saving}
          />
        </div>
        <div className="field">
          <label>Code postal</label>
          <input
            value={payload.addressZip || ""}
            onChange={(e) => onChange("addressZip", e.target.value)}
            disabled={loading || saving}
          />
        </div>
        <div className="field">
          <label>Pays</label>
          <input
            value={payload.addressCountry || ""}
            onChange={(e) => onChange("addressCountry", e.target.value)}
            disabled={loading || saving}
          />
        </div>

        <div className="field span2">
          <label>Horaires</label>
          <textarea
            rows={3}
            placeholder={"Lun-Ven 9:00–18:00\nSam 10:00–14:00\nDim fermé"}
            value={payload.hoursText || ""}
            onChange={(e) => onChange("hoursText", e.target.value)}
            disabled={loading || saving}
          />
        </div>

        <div className="field span2">
          <label>Catégories (séparées par des virgules)</label>
          <input
            placeholder="ex: restaurant, bar"
            value={categoriesText}
            onChange={(e) => onCategoriesTextChange(e.target.value)}
            disabled={loading || saving}
          />
          <div className="help">
            Suggestions&nbsp;: {catSuggestion.join(", ")}
          </div>
        </div>

        <div className="field">
          <label>Facebook</label>
          <input
            placeholder="https://facebook.com/…"
            value={payload.facebook || ""}
            onChange={(e) => onChange("facebook", e.target.value)}
            disabled={loading || saving}
          />
        </div>
        <div className="field">
          <label>Instagram</label>
          <input
            placeholder="https://instagram.com/…"
            value={payload.instagram || ""}
            onChange={(e) => onChange("instagram", e.target.value)}
            disabled={loading || saving}
          />
        </div>
        <div className="field">
          <label>TikTok</label>
          <input
            placeholder="https://tiktok.com/@…"
            value={payload.tiktok || ""}
            onChange={(e) => onChange("tiktok", e.target.value)}
            disabled={loading || saving}
          />
        </div>
        <div className="field">
          <label>WhatsApp</label>
          <input
            placeholder="+33 6 12 34 56 78"
            value={payload.whatsapp || ""}
            onChange={(e) => onChange("whatsapp", e.target.value)}
            disabled={loading || saving}
          />
        </div>

        <div className="field">
          <label>
            <input
              type="checkbox"
              checked={!!payload.allowIndexing}
              onChange={(e) => onChange("allowIndexing", e.target.checked)}
              disabled={loading || saving}
            />{" "}
            Autoriser l’indexation par les moteurs de recherche
          </label>
        </div>
      </div>

      <div className="actions">
        <button onClick={onSave} disabled={loading || saving}>
          {saving ? "Enregistrement…" : "Enregistrer"}
        </button>
        {payload.slug && (
          <>
            <Link
              className="secondary"
              to={`/p/${payload.slug}`}
              target="_blank"
              rel="noreferrer"
            >
              Site web
            </Link>
            <Link
              className="secondary"
              to={`/b/${payload.slug}`}
              target="_blank"
              rel="noreferrer"
            >
              Page avis
            </Link>
          </>
        )}
        <Link className="secondary" to="/directory">
          Ouvrir l’annuaire
        </Link>
      </div>
    </div>
  );
};

export default PublicProfileForm;
